'use client';

import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ImageZoom } from '../../components/image-zoom';
import { cn } from '../../utils/cn';
import {
  formatImageAlt,
  resolvePublicProfileModuleChrome,
  type PublicProfileModuleChrome,
} from '../public-profile-module-copy';

interface ImagesModuleDisplayProps {
  module: ProfileModuleDto;
  /** Optional: resolve relative file paths to full URLs. */
  getFileUrl?: (path: string) => string;
  chrome?: PublicProfileModuleChrome;
}

type ImageItem = {
  id?: string;
  file: {
    id: string;
    path: string;
    [key: string]: unknown;
  };
  title?: string;
  description?: string;
  order?: number;
  visible?: boolean;
};

type ImagesAspectRatio = '1:1' | '4:3' | '16:9' | '3:2' | '3:4';

type ImagesModuleConfig = {
  type: 'images';
  images: ImageItem[];
  aspectRatio?: ImagesAspectRatio;
};

const GAP = 12;
const CARD_WIDTH = 288; // w-72; use 256 for w-64 on small screens if needed
const SCROLL_STEP = CARD_WIDTH + GAP;

const ASPECT_RATIO_CLASSES: Record<
  ImagesAspectRatio,
  { container: string; img: string }
> = {
  '1:1': { container: 'aspect-square', img: 'object-cover' },
  '4:3': { container: 'aspect-[4/3]', img: 'object-cover' },
  '16:9': { container: 'aspect-video', img: 'object-cover' },
  '3:2': { container: 'aspect-[3/2]', img: 'object-cover' },
  '3:4': { container: 'aspect-[3/4]', img: 'object-cover' },
};

function getImagesConfig(
  rawConfig: Record<string, unknown>,
): ImagesModuleConfig {
  const allowed: ImagesAspectRatio[] = ['1:1', '4:3', '16:9', '3:2', '3:4'];
  const aspectRatio =
    typeof rawConfig.aspectRatio === 'string' &&
    allowed.includes(rawConfig.aspectRatio as ImagesAspectRatio)
      ? (rawConfig.aspectRatio as ImagesAspectRatio)
      : '1:1';
  return {
    type: 'images',
    images: (rawConfig.images as ImageItem[]) || [],
    aspectRatio,
  };
}

/**
 * Displays images in a horizontal slider (scrollable, no scrollbar). Desktop: prev/next buttons. Click to zoom (shadcn ImageZoom).
 */
export function ImagesModuleDisplay({
  module,
  getFileUrl = (path) => path,
  chrome,
}: ImagesModuleDisplayProps) {
  const copy = chrome ?? resolvePublicProfileModuleChrome(undefined);
  const config = getImagesConfig(module.config || {});
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft < el.scrollWidth - el.clientWidth - 2;
    setCanScrollLeft(left);
    setCanScrollRight(right);
  }, []);

  const scroll = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === 'left' ? -SCROLL_STEP : SCROLL_STEP,
      behavior: 'smooth',
    });
  }, []);

  const visibleImages = (config.images || [])
    .filter((img) => img.visible !== false && img.file?.path)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateScrollState, visibleImages.length]);

  if (visibleImages.length === 0) return null;

  const ratio = config.aspectRatio ?? '1:1';
  const { container: aspectContainerClass, img: aspectImgClass } =
    ASPECT_RATIO_CLASSES[ratio];

  return (
    <div className="relative w-full min-w-0">
      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        className="min-h-0 w-full overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div className="flex min-h-0 gap-3">
          {visibleImages.map((image, index) => (
            // Aspect ratio lives on an outer frame. ImageZoom's internal wrappers
            // must fill that frame — putting aspect-* on ImageZoom alone lets the
            // natural image size win (1:1 config ignored).
            <div
              key={image.file.id ?? index}
              className={cn(
                'relative w-64 shrink-0 overflow-hidden rounded-lg sm:w-72',
                aspectContainerClass,
              )}
              data-profile-image-frame
              data-aspect-ratio={ratio}
            >
              <ImageZoom
                className={cn(
                  'absolute inset-0 size-full',
                  '[&_[data-rmiz]]:size-full',
                  '[&_[data-rmiz-content]]:size-full',
                  '[&_[data-rmiz-content]_img]:size-full',
                )}
              >
                <img
                  src={getFileUrl(image.file.path)}
                  alt={
                    image.title ||
                    formatImageAlt(copy.imageAltFallback, index + 1)
                  }
                  className={cn('size-full', aspectImgClass)}
                />
              </ImageZoom>
            </div>
          ))}
        </div>
      </div>

      {/* Prev/next buttons */}
      {visibleImages.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            aria-label={copy.previousImage}
            className="absolute left-0 top-1/2 z-10 flex size-10 -translate-y-1/2 items-center justify-center rounded-full border border-border/50 bg-background/90 text-foreground shadow-md transition-opacity hover:bg-background disabled:pointer-events-none disabled:opacity-30"
          >
            <ChevronLeft className="size-5" />
          </button>
          <button
            type="button"
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            aria-label={copy.nextImage}
            className="absolute right-0 top-1/2 z-10 flex size-10 -translate-y-1/2 items-center justify-center rounded-full border border-border/50 bg-background/90 text-foreground shadow-md transition-opacity hover:bg-background disabled:pointer-events-none disabled:opacity-30"
          >
            <ChevronRight className="size-5" />
          </button>
        </>
      )}
    </div>
  );
}
